import React, {useEffect, useState} from "react";
import axios from "axios";
import styles from "./AdicionarEndereco.module.css";
import {useTranslation} from "react-i18next";
import api from "../../utils/axios/api";

interface State {
    id: number;
    nome: string;
    regiao: {
        id: number;
        nome: string;
        sigla: string;
    };
    sigla: string;
}

interface City {
    id: number;
    nome: string;
}

interface Address {
    name?: string;
    zipCode: string;
    street: string;
    number: string;
    complement: string;
    district: string;
    state: string;
    city: string;
    clientId: string;
}

function AdicionarEndereco() {
    const {t} = useTranslation();
    const [estados, setEstados] = useState<State[]>([]);
    const [cities, setCities] = useState<City[]>([]);
    const [address, setAddress] = useState<Address>({
        name: "",
        zipCode: "",
        street: "",
        number: "",
        complement: "",
        district: "",
        state: "",
        city: "",
        clientId: "" // Inicializamos vazio
    });

    useEffect(() => {
        async function fetchInitialData() {
            try {
                const responseEstados = await axios.get(
                    "https://servicodados.ibge.gov.br/api/v1/localidades/estados"
                );
                setEstados(responseEstados.data);
            } catch (error) {
                console.error("Erro ao buscar estados:", error);
            }
        }

        fetchInitialData();
    }, []);

    const fetchCities = async (stateSigla: string) => {
        try {
            const response = await axios.get(
                `https://servicodados.ibge.gov.br/api/v1/localidades/estados/${stateSigla}/municipios`
            );
            setCities(response.data);
        } catch (error) {
            console.error("Erro ao buscar cidades:", error);
        }
    };

    const handleInputChange = (field: keyof Address, value: string) => {
        setAddress((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleStateChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const state = event.target.value;
        setAddress((prev) => ({
            ...prev,
            state,
        }));
        fetchCities(state);
    };

    const handleCepBlur = async (event: React.FocusEvent<HTMLInputElement>) => {
        const cep = event.target.value.replace(/\D/g, "");
        if (cep.length === 8) {
            try {
                const response = await axios.get(`https://viacep.com.br/ws/${cep}/json/`);
                const data = response.data;
                if (!data.erro) {
                    setAddress((prev) => ({
                        ...prev,
                        street: data.logradouro,
                        district: data.bairro,
                        city: data.localidade,
                        state: data.uf,
                        zipCode: cep,
                    }));
                    fetchCities(data.uf);
                } else {
                    console.error("CEP não encontrado.");
                }
            } catch (error) {
                console.error("Erro ao buscar dados do CEP:", error);
            }
        }
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const clientId = localStorage.getItem("clientId") || ""; // Recuperação dinâmica
        try {
            const response = await api.post("/address", {
                ...address,
                clientId,
            });
            console.log("Endereço cadastrado com sucesso:", response.data);
            window.alert(t("accountMenu.addressAdded")); // Mensagem traduzida
            window.location.reload();
        } catch (error) {
            console.error("Erro ao cadastrar o endereço:", error);
            let detail = "";
            if (axios.isAxiosError(error) && error.response?.data) {
                const data = error.response.data as {
                    message?: string;
                    error?: string;
                };
                detail = data.message ?? data.error ?? "";
            }
            window.alert(
                detail
                    ? `${t("accountMenu.addressAddError")}\n\n${detail}`
                    : t("accountMenu.addressAddError")
            );
        }
    };

    return (
        <div className={styles.main_container}>
            <h1>{t(`accountMenu.editAddress`)}</h1>
            <form className={styles.campo} onSubmit={handleSubmit}>
                <div className={styles.roll}>
                    <div className={styles.inputContainer}>
                        <label htmlFor="editAddressName">{t(`accountMenu.nickname`)}</label>
                        <input
                            id="editAddressName"
                            type="text"
                            placeholder="Casa"
                            value={address.name ?? ""}
                            onChange={(e) => handleInputChange("name", e.target.value)}
                        />
                    </div>
                    <div className={styles.inputContainer}>
                        <label htmlFor="editAddressZipCode" style={{textTransform: "uppercase"}}>
                            {t(`accountMenu.cep`)}
                        </label>
                        <input
                            id="editAddressZipCode"
                            type="text"
                            onBlur={handleCepBlur}
                            placeholder="12345678"
                            value={address.zipCode}
                            onChange={(e) => handleInputChange("zipCode", e.target.value)}
                        />
                    </div>
                </div>
                <div className={styles.roll}>
                    <div className={styles.inputContainer}>
                        <label htmlFor="editAddressStreet">{t(`accountMenu.address`)}</label>
                        <input
                            id="editAddressStreet"
                            type="text"
                            placeholder="Rua James Cameron"
                            value={address.street}
                            onChange={(e) => handleInputChange("street", e.target.value)}
                        />
                    </div>
                    <div className={styles.NumAndComp}>
                        <div className={styles.inputContainer}>
                            <label htmlFor="editAddressNumber">{t(`accountMenu.houseNumber`)}</label>
                            <input
                                id="editAddressNumber"
                                type="text"
                                placeholder="Ex: 123 ou S/N"
                                value={address.number}
                                onChange={(e) => handleInputChange("number", e.target.value)}
                            />
                        </div>
                        <div className={styles.inputContainer}>
                            <label htmlFor="editAddressComplement">{t(`cadastro.complement`)}</label>
                            <input
                                id="editAddressComplement"
                                type="text"
                                placeholder="Kitnet 123"
                                value={address.complement}
                                onChange={(e) => handleInputChange("complement", e.target.value)}
                            />
                        </div>
                    </div>
                </div>
                <div className={styles.roll}>
                    <div className={styles.inputContainer}>
                        <label htmlFor="selectState">{t(`accountMenu.state`)}</label>
                        <select
                            id="selectState"
                            className={styles.select}
                            value={address.state}
                            onChange={handleStateChange}
                        >
                            <option value="" disabled>
                                {t(`accountMenu.select`)}
                            </option>
                            {estados.map((estado) => (
                                <option key={estado.id} value={estado.sigla}>
                                    {estado.sigla}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className={styles.inputContainer}>
                        <label htmlFor="selectCity">{t(`accountMenu.city`)}</label>
                        <select
                            id="selectCity"
                            className={styles.select}
                            value={address.city}
                            onChange={(e) => handleInputChange("city", e.target.value)}
                            disabled={!address.state}
                        >
                            <option value="" disabled>
                                {t(`accountMenu.select`)}
                            </option>
                            {cities.map((city) => (
                                <option key={city.id} value={city.nome}>
                                    {city.nome}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
                <div className={styles.buttonContainer}>
                    <button type="submit">{t(`accountMenu.addNewAddress`)}</button>
                </div>
            </form>
        </div>
    );
}

export default AdicionarEndereco;
